import type { ReactNode } from "react";
import { AxiosError } from "axios";
import { useCarrierMe } from "@/api/carrier";
import { PortalState } from "@/components/portal-state";
import { Button } from "@/components/ui/button";
import { useSession } from "@/services/auth0/session";

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Gate every screen behind a valid session. Until Auth0 is configured
 * (env vars present + SPA app provisioned), `isLoggedIn` will be false and
 * we render an unauthenticated landing inline rather than redirect — that
 * keeps the dev smoke loop simple (no infinite redirect loops while Auth0
 * is null).
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isLoading, isLoggedIn, login, switchAccount } = useSession();
  const carrierMe = useCarrierMe();

  if (isLoading || (isLoggedIn && carrierMe.isLoading)) {
    return <PortalState title="Loading carrier portal" loading />;
  }

  if (!isLoggedIn) {
    return (
      <PortalState
        title="EZX Carrier Portal"
        description={
          <p>
            Sign in to access trips, tasks, shipments, and your team. Auth0
            configuration is required for sign-in to work.
          </p>
        }
      >
        <Button type="button" onClick={() => login()} size="touch">
          Sign in
        </Button>
      </PortalState>
    );
  }

  if (carrierMe.isError || !carrierMe.data?.capabilities.canAccessCarrierWeb) {
    const message = getCarrierAccessError(carrierMe.error);
    return (
      <PortalState
        title="Carrier access required"
        description={<p>{message}</p>}
      >
        <Button type="button" onClick={switchAccount} size="touch">
          Sign in with another account
        </Button>
      </PortalState>
    );
  }

  return <>{children}</>;
}

function getCarrierAccessError(error: unknown): string {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    if (status === 401) {
      return "This login is not linked to an active carrier team account. Ask an EZX admin or your carrier admin for an invite.";
    }
    if (status === 403) {
      return "This carrier account is inactive or does not have access to this app.";
    }
  }
  return "This account cannot access the carrier portal.";
}
